import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { useLoader } from '../../../hooks/useLoader';
import { PurposeBuiltNodeDetails, useNodeDetails } from '../../../hooks/useNodeDetails';
import { LOADER_DEBOUNCE_MS } from '../../../const/global-constants';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Button, MenuItem, Pagination, Select, Tag, Tile } from '@dsi/react-eds';
import styles from './NodeActivityStatus.module.css';
import { useTheme } from '../../../hooks/useTheme';
import api from '../../../api/axiosInstance';

const statusColorMap: Record<string, string> = {
  upgrade_success: 'green',
  upgrade_failed: 'red',
  upgrade_skipped: 'yellow',
  rollback_success: 'blue',
  rollback_failed: 'red'
};

function NodeActivityStatus({ workflowUuid }: { workflowUuid: string }) {
  const { addNotifications } = useSwobNotifications();
  const { theme } = useTheme();

  const [hasError, setHasError] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });

  const hasActiveFilters = columnFilters.length > 0;
  const columns = useMemo<ColumnDef<PurposeBuiltNodeDetails>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name'
      },
      {
        accessorKey: 'basebandType',
        header: 'Node Type'
      },
      {
        accessorKey: 'status',
        header: 'Status',
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue?.length) return true;
          return filterValue.includes(row.getValue(columnId));
        },
        cell: ({ getValue }) => {
          const status = getValue() as string;
          const label = status
            .split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
          return (
            <ReactEDSWrapper component={Tag} color={statusColorMap[status] || 'gray'} big>
              {label}
            </ReactEDSWrapper>
          );
        }
      },
      {
        id: 'Actions',
        header: 'Actions',
        cell: ({ row }) => {
          const logUrl = row.original.logUrl;
          const hasLogUrl = logUrl && logUrl.trim() !== '';
          return (
            <div className={styles.actionButtons}>
              <ReactEDSWrapper
                component={Button}
                icon="download-save"
                aria-label={`Download Log`}
                title="Download log"
                disabled={!hasLogUrl}
                onClick={async () => {
                  if (hasLogUrl) {
                    try {
                      showLoader();
                      const response = await api.post('/api/workflows/logs/download', { logUrl }, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${row.original.name}-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      console.error(`Èrror: ${err}`);
                      addNotifications([
                        {
                          title: 'Error - Node Activity Status',
                          icon: 'icon-triangle-warning',
                          description: `Failed to download logs for ${row.original.name}.`,
                          timestamp: new Date(),
                          color: 'red'
                        }
                      ]);
                    } finally {
                      setTimeout(() => {
                        hideLoader();
                      }, 500);
                    }
                  }
                }}
              />
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: false
      }
      // Columns are static — no reactive dependencies
       
    ],
    []
  );

  const query = useNodeDetails(workflowUuid);

  const table = useReactTable({
    data: query.data ?? [],
    columns,
    state: { columnFilters, pagination },
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  useEffect(() => {
    if (query.isFetching) {
      showLoader();
    } else {
      const timer = setTimeout(hideLoader, LOADER_DEBOUNCE_MS);
      return () => clearTimeout(timer);
    }
  }, [query.isFetching]);

  useEffect(() => {
    if (query.error && !hasError) {
      setHasError(true);
      addNotifications([
        {
          title: 'Error - Deployment Summary',
          icon: 'icon-triangle-warning',
          description: 'An error occurred while fetching Purpose Built node details.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } else if (!query.error && hasError) {
      setHasError(false);
    }
  }, [query.error]);

  return (
    <ReactEDSWrapper component={Tile} title="Node Activity Status" id="node-activity-status">
      {query.isSuccess && query.data.length === 0 && !hasActiveFilters ? (
        <div className={styles.noDataContainer}>
          <i className={`icon icon-info ${styles.dangerIcon}`} />
          <div className={styles.noDataTitle}>No node activity data available</div>
        </div>
      ) : (
        <>
          <div className={styles.header}>
            {table.getPrePaginationRowModel().rows.length > 0 && (
              <span className={`${styles.itemCount} ${!theme ? styles.itemCountDark : ''}`}>
                {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                  table.getPrePaginationRowModel().rows.length
                )}{' '}
                of {table.getPrePaginationRowModel().rows.length} items
              </span>
            )}
            <div className={styles.buttonGroup}>
              {!query.isError && (
                <ReactEDSWrapper
                  component={Button}
                  icon={showFilters ? 'filter-off' : 'filter'}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? 'Close Filters' : 'Filters'}
                </ReactEDSWrapper>
              )}
              {showFilters && (
                <ReactEDSWrapper
                  component={Button}
                  icon="filter-clear"
                  onClick={() => {
                    setColumnFilters([]);
                    setPagination({ pageIndex: 0, pageSize: 10 });
                  }}
                >
                  Clear Filters
                </ReactEDSWrapper>
              )}
            </div>
          </div>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className={styles.headerRow}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} className={styles.headerCell}>
                        <div
                          className={header.column.getCanSort() ? styles.sortableHeader : undefined}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className={styles.sortIcon}>
                              {header.column.getIsSorted() === 'asc' ? (
                                <i className={`icon icon-sort-up ${styles.sortAsc}`} />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <i className="icon icon-sort-down" />
                              ) : (
                                <i className="icon icon-sorting" />
                              )}
                            </span>
                          )}
                        </div>
                        {header.column.getCanFilter() && showFilters ? (
                          <div className={styles.filterContainer}>
                            {header.column.id === 'status' ? (
                              <ReactEDSWrapper
                                component={Select}
                                type="multi"
                                label="Select options"
                                className={styles.filterSelect}
                                value={header.column.getFilterValue() || []}
                                onChange={(event: React.FormEvent<HTMLElement>) => {
                                  header.column.setFilterValue(event);
                                }}
                              >
                                <ReactEDSWrapper component={MenuItem} value="upgrade_success">
                                  Upgrade Success
                                </ReactEDSWrapper>
                                <ReactEDSWrapper component={MenuItem} value="upgrade_failed">
                                  Upgrade Failed
                                </ReactEDSWrapper>
                                <ReactEDSWrapper component={MenuItem} value="upgrade_skipped">
                                  Upgrade Skipped
                                </ReactEDSWrapper>
                                <ReactEDSWrapper component={MenuItem} value="rollback_success">
                                  Rollback Success
                                </ReactEDSWrapper>
                                <ReactEDSWrapper component={MenuItem} value="rollback_failed">
                                  Rollback Failed
                                </ReactEDSWrapper>
                              </ReactEDSWrapper>
                            ) : (
                              <input
                                type="text"
                                value={(header.column.getFilterValue() ?? '') as string}
                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                placeholder={`Filter`}
                                className={styles.filterInput}
                              />
                            )}
                          </div>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={table.getAllColumns().length} className={styles.noDataContainer}>
                      <i className={`icon icon-info ${styles.dangerIcon}`} />
                      <div className={styles.noDataTitle}>
                        {query.isError ? 'Failed to retrieve node activity data.' : 'No results match your filters'}
                      </div>
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className={styles.tableRow}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={`${styles.tableCell} ${!theme ? styles.tableCellDark : ''}`}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {table.getPrePaginationRowModel().rows.length > 0 && (
            <ReactEDSWrapper
              component={Pagination}
              numPages={table.getPageCount()}
              selectedPage={table.getState().pagination.pageIndex + 1}
              onPaginationChangePage={(page: number) => {
                table.setPageIndex(page - 1);
              }}
              optionsList={[
                { value: '10', label: '10', active: table.getState().pagination.pageSize === 10 },
                { value: '20', label: '20', active: table.getState().pagination.pageSize === 20 },
                { value: '30', label: '30', active: table.getState().pagination.pageSize === 30 },
                { value: '40', label: '40', active: table.getState().pagination.pageSize === 40 },
                { value: '50', label: '50', active: table.getState().pagination.pageSize === 50 }
              ]}
              onPaginationChangeSelect={(size: string) => {
                table.setPageSize(Number(size));
                table.setPageIndex(0);
              }}
              withActions
            />
          )}
        </>
      )}
    </ReactEDSWrapper>
  );
}
export default NodeActivityStatus;
